import React, { useMemo } from "react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Checkbox, Popover, Tooltip, Typography, message } from "antd";

const { Text } = Typography;

const FUNCTIONAL_COLUMN_WARNINGS = {
    actions: "隐藏后无法在表格内执行行操作，包括新增、删除和弹窗编辑。",
    flightRouteId: "隐藏后无法在表格内编辑航路。",
    constraintStatus: "隐藏后无法看到校验结果。",
};

function getColumnLabel(column) {
    if (typeof column?.name === "string") {
        return column.name;
    }
    if (typeof column?.title === "string") {
        return column.title;
    }
    return column?.key || "未命名列";
}

function getOrderedColumnKeys(columns = [], columnOrder = []) {
    const defaultKeys = columns.map((column) => column.key).filter(Boolean);
    const defaultKeySet = new Set(defaultKeys);
    const orderedKnownKeys = [];

    (Array.isArray(columnOrder) ? columnOrder : []).forEach((key) => {
        if (defaultKeySet.has(key) && !orderedKnownKeys.includes(key)) {
            orderedKnownKeys.push(key);
        }
    });

    defaultKeys.forEach((key) => {
        if (!orderedKnownKeys.includes(key)) {
            orderedKnownKeys.push(key);
        }
    });

    return orderedKnownKeys;
}

function SortableColumnSettingRow({ column, visible, onVisibleChange }) {
    const label = getColumnLabel(column);
    const warning = FUNCTIONAL_COLUMN_WARNINGS[column.key];
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: column.key });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} className="schedule-draft-timetable-column-setting-row">
            <Tooltip title={`拖拽调整 ${label} 列顺序`}>
                <button
                    type="button"
                    className="schedule-draft-timetable-column-setting-drag-handle"
                    aria-label={`拖拽调整 ${label} 列顺序`}
                    {...attributes}
                    {...listeners}
                >
                    <DragOutlined />
                </button>
            </Tooltip>
            <Checkbox checked={visible} onChange={(event) => onVisibleChange(column, event.target.checked)}>
                {label}
            </Checkbox>
            {warning ? (
                <Tooltip title={warning}>
                    <Text type="secondary" className="schedule-draft-timetable-column-setting-impact">功能列</Text>
                </Tooltip>
            ) : null}
        </div>
    );
}

const TimetableColumnSettingsPopover = ({ columns = [], columnOrder = [], hiddenColumnKeys = [], onColumnOrderChange, onHiddenColumnKeysChange }) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
    );
    const orderedColumnKeys = useMemo(() => getOrderedColumnKeys(columns, columnOrder), [columnOrder, columns]);
    const columnsByKey = useMemo(() => new Map(columns.map((column) => [column.key, column])), [columns]);
    const hiddenColumnKeySet = useMemo(() => new Set(hiddenColumnKeys), [hiddenColumnKeys]);
    const orderedColumns = useMemo(
        () => orderedColumnKeys.map((key) => columnsByKey.get(key)).filter(Boolean),
        [columnsByKey, orderedColumnKeys],
    );
    const defaultColumnKeys = useMemo(() => columns.map((column) => column.key).filter(Boolean), [columns]);

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = orderedColumnKeys.indexOf(active.id);
        const newIndex = orderedColumnKeys.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) {
            return;
        }

        onColumnOrderChange?.(arrayMove(orderedColumnKeys, oldIndex, newIndex));
    };

    const handleVisibleChange = (column, checked) => {
        const currentHiddenKeys = orderedColumnKeys.filter((key) => hiddenColumnKeySet.has(key));
        const nextHiddenKeys = checked
            ? currentHiddenKeys.filter((key) => key !== column.key)
            : [...currentHiddenKeys, column.key];

        onHiddenColumnKeysChange?.(nextHiddenKeys);

        if (!checked) {
            const label = getColumnLabel(column);
            const warning = FUNCTIONAL_COLUMN_WARNINGS[column.key];
            message.warning(`${label} 列已隐藏，可通过列设置恢复。${warning ? ` ${warning}` : ""}`);
        }
    };

    const handleReset = () => {
        onColumnOrderChange?.(defaultColumnKeys);
        onHiddenColumnKeysChange?.([]);
        message.success("列设置已恢复默认");
    };

    const content = (
        <div className="schedule-draft-timetable-column-settings">
            <div className="schedule-draft-timetable-column-settings-header">
                <div>
                    <Text strong>列设置</Text>
                    <Text type="secondary">拖拽排序，取消勾选可隐藏列。</Text>
                </div>
                <Button size="small" type="link" onClick={handleReset}>重置默认</Button>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={orderedColumnKeys} strategy={verticalListSortingStrategy}>
                    <div className="schedule-draft-timetable-column-setting-list">
                        {orderedColumns.map((column) => (
                            <SortableColumnSettingRow
                                key={column.key}
                                column={column}
                                visible={!hiddenColumnKeySet.has(column.key)}
                                onVisibleChange={handleVisibleChange}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
            <Text type="secondary" className="schedule-draft-timetable-column-settings-note">
                隐藏后不会删除数据，刷新页面后会保留当前偏好。
            </Text>
        </div>
    );

    return (
        <Popover trigger="click" placement="bottomRight" content={content}>
            <Button size="small" icon={<SettingOutlined />}>列设置</Button>
        </Popover>
    );
};

export default TimetableColumnSettingsPopover;
